/**
 * The HOST's answer to `pdapp.links.page()` (`papan:pageLink`) inside Papan.
 *
 * The app asks for the address of one of its OWN pages (a QR code: "open the remote
 * on your phone"). The address grants nothing — whoever opens it meets the normal
 * access check — so nothing here asks the person anything, and nothing makes a link:
 *
 *   1. the app folder already has its APP default link and this person may hand it
 *      out (`GET /api/share-links/default-app-origin`, the server's
 *      `mayUseDefaultLink`) → that link's origin + the page; reach `'link'`;
 *   2. otherwise Papan's own address of the app: `<this SPA's origin>/t/<tenant>/app/
 *      <ref>/<page>?<query>#<hash>` (the desktop and phone routes both open it);
 *      reach `'access'` — only people who already have access in Papan open it.
 *      ONLY for the viewer's own tenant's content (`ownContent`): an app reached
 *      through a `.shared` / `.chain` (#327) — or by a compound mount id — would read
 *      the VIEWER's tenant id and their pointer row's id in that address, which an app
 *      of another org may never learn without consent (principle 10). There the
 *      answer is `unavailable`.
 *
 * What the frame sent is UNTRUSTED: it is validated again with the one shared rule.
 * The app is the host's own binding (`app`), never anything the frame names.
 */
import { appPageParts, appPageUrl, type AppPageLink, type AppPageLinkProblem } from '@papan/types/app-page-link'
import { namedTenantPathPrefix, isMountFileId } from '@papan/types'

/** Which app this viewer runs, as the SPA's `/app/<ref>` route opens it. */
export interface AppPageRef {
  /** The id the `/app/<ref>` route takes: a PDApp's folder, or a page's HTML file. */
  ref: string
  kind: 'miniapp' | 'html'
  /** The tenant the app runs in (the shell's own); absent → the caller's fallback. */
  tenantId?: string | null
  /** The app's row is the shell tenant's OWN content — not reached through a pointer
   *  (`.shared` / `.chain`) or a compound mount id. Set it with `isOwnTenantAppRow`
   *  once the row is resolved; false (not yet known) answers no Papan address. */
  ownContent: boolean
}

/** Is the row a surface opened by `ref` the shell tenant's own content? False for a
 *  compound mount id and for a `.shared` / `.chain` pointer row (#327): Papan's
 *  address there would name the viewer's tenant and pointer to the app. */
export function isOwnTenantAppRow(ref: string, fileType: string | null | undefined): boolean {
  return !isMountFileId(ref) && fileType !== 'shared' && fileType !== 'chain'
}

export type PageLinkAnswer = AppPageLink | { error: AppPageLinkProblem | 'unavailable'; message: string }

export interface PageLinkAnswerDeps {
  /** The app this viewer is bound to, or null where the surface offers no page address. */
  app: AppPageRef | null
  /** The tenant to fall back on when `app.tenantId` is absent (the active one). */
  fallbackTenantId: string | null
  /** This SPA's own origin (`location.origin`). */
  frontendOrigin: string
  /** The app link's origin when one exists that the person may hand out, else null.
   *  May throw — a failed lookup falls back to Papan's own address. */
  linkOriginFor: (folderId: string) => Promise<string | null>
}

const UNAVAILABLE = 'pdapp.links.page(): there is no address for the app\'s pages here.'

export async function answerPageLink(data: { page?: unknown; query?: unknown; hash?: unknown }, deps: PageLinkAnswerDeps): Promise<PageLinkAnswer> {
  const checked = appPageParts(data.page, { query: data.query ?? undefined, hash: data.hash ?? undefined })
  if (!checked.ok) return { error: checked.problem, message: checked.message }
  const { app } = deps
  if (!app || !app.ref) return { error: 'unavailable', message: UNAVAILABLE }

  // 1. The app's existing app link — PDApps only (a loose page's app link is rooted at
  //    its folder, and the page path would not mean the same thing there).
  if (app.kind === 'miniapp') {
    let origin: string | null = null
    try { origin = await deps.linkOriginFor(app.ref) } catch { origin = null }
    if (origin) {
      try { return { url: appPageUrl(origin, checked.parts), reach: 'link' } } catch { /* not an http(s) origin → fall through */ }
    }
  }

  // 2. Papan's own address of the app — always the NAMED tenant: the reader is whoever
  //    scans it, not this person (`/~` would be the scanner's own drive). Only for the
  //    viewer's own tenant's content: through a pointer the address would hand the app
  //    the viewer's tenant id and pointer row id (principle 10).
  if (!app.ownContent || !isOwnTenantAppRow(app.ref, null)) return { error: 'unavailable', message: UNAVAILABLE }
  const tenantPath = namedTenantPathPrefix(app.tenantId || deps.fallbackTenantId)
  if (!tenantPath) return { error: 'unavailable', message: UNAVAILABLE }
  try {
    const base = `${deps.frontendOrigin.replace(/\/+$/, '')}${tenantPath}/app/${encodeURIComponent(app.ref)}`
    return { url: appPageUrl(base, checked.parts), reach: 'access' }
  } catch {
    return { error: 'unavailable', message: UNAVAILABLE }
  }
}
